// Shared PWA alert template renderer.
// Keep the relative-day logic in sync with cron_notify_gelanggang.php (asmm_bila()).

export type AlertEventKey = 'latihan' | 'acara' | 'ujian' | 'mesyuarat';

export interface AlertEventTemplate {
  title: string;
  body: string;
}

export const ALERT_EVENT_TYPES: { key: AlertEventKey; label: string; emoji: string }[] = [
  { key: 'latihan', label: 'Sesi Latihan', emoji: '🥋' },
  { key: 'acara', label: 'Acara / Program', emoji: '📅' },
  { key: 'ujian', label: 'Ujian Bengkung', emoji: '🎖️' },
  { key: 'mesyuarat', label: 'Mesyuarat', emoji: '🤝' },
];

export const DEFAULT_EVENT_TEMPLATES: Record<AlertEventKey, AlertEventTemplate> = {
  latihan: {
    title: '🥋 Peringatan Gelanggang: {gelanggang}',
    body: 'Sesi latihan bermula {bila} ({tarikh}) jam {waktu}. Sila sahkan kehadiran (RSVP) anda segera!',
  },
  acara: {
    title: '📅 {tajuk}',
    body: '{tajuk} akan berlangsung {bila} ({tarikh}) jam {waktu} di {lokasi}. Sahkan kehadiran anda sekarang!',
  },
  ujian: {
    title: '🎖️ Ujian Bengkung: {gelanggang}',
    body: 'Ujian Kenaikan Bengkung {bila} ({tarikh}) jam {waktu} di {lokasi}. Pastikan pendaftaran & persiapan lengkap!',
  },
  mesyuarat: {
    title: '🤝 Mesyuarat: {tajuk}',
    body: 'Mesyuarat diadakan {bila} ({tarikh}) jam {waktu} di {lokasi}. Mohon hadir tepat pada masanya.',
  },
};

export const ALERT_TEMPLATE_VARIABLES: { key: string; desc: string }[] = [
  { key: '{bila}', desc: 'Hari ini / esok / lusa / dalam 4 hari / minggu depan' },
  { key: '{tarikh}', desc: 'Contoh: Sabtu, 17 Okt' },
  { key: '{hari}', desc: 'Nama hari (Sabtu)' },
  { key: '{waktu}', desc: 'Masa mula (8:00 AM)' },
  { key: '{baki_jam}', desc: 'Baki jam sebelum mula' },
  { key: '{baki_hari}', desc: 'Baki hari sebelum mula' },
  { key: '{gelanggang}', desc: 'Nama gelanggang' },
  { key: '{tajuk}', desc: 'Tajuk acara / notis' },
  { key: '{lokasi}', desc: 'Lokasi acara' },
  { key: '{nama_ahli}', desc: 'Nama penerima' },
];

const DAYS_MS = ['Ahad', 'Isnin', 'Selasa', 'Rabu', 'Khamis', 'Jumaat', 'Sabtu'];
const MONTHS_MS = ['Jan', 'Feb', 'Mac', 'Apr', 'Mei', 'Jun', 'Jul', 'Ogo', 'Sep', 'Okt', 'Nov', 'Dis'];

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Calendar-day difference (not 24h blocks), so 11pm → 8am next day is still "esok". */
export function calendarDayDiff(eventDate: Date, now: Date = new Date()): number {
  return Math.round((startOfDay(eventDate).getTime() - startOfDay(now).getTime()) / 86400000);
}

/** Relative day phrase in Malay, lower-case so it fits mid-sentence. */
export function formatRelativeDay(eventDate: Date, now: Date = new Date()): string {
  const diff = calendarDayDiff(eventDate, now);
  if (diff < 0) return diff === -1 ? 'semalam' : `${Math.abs(diff)} hari lepas`;
  if (diff === 0) return 'hari ini';
  if (diff === 1) return 'esok';
  if (diff === 2) return 'lusa';
  if (diff <= 6) return `dalam ${diff} hari`;
  if (diff <= 13) return 'minggu depan';
  return `dalam ${diff} hari`;
}

export function formatShortDate(d: Date): string {
  return `${DAYS_MS[d.getDay()]}, ${d.getDate()} ${MONTHS_MS[d.getMonth()]}`;
}

export interface AlertTemplateVars {
  eventDate: Date;
  now?: Date;
  waktu?: string;
  gelanggang?: string;
  tajuk?: string;
  lokasi?: string;
  namaAhli?: string;
}

export function renderAlertTemplate(template: string, vars: AlertTemplateVars): string {
  const now = vars.now || new Date();
  const hoursLeft = Math.max(0, Math.round((vars.eventDate.getTime() - now.getTime()) / 3600000));
  const map: Record<string, string> = {
    '{bila}': formatRelativeDay(vars.eventDate, now),
    '{tarikh}': formatShortDate(vars.eventDate),
    '{hari}': DAYS_MS[vars.eventDate.getDay()],
    '{waktu}': vars.waktu || '',
    '{baki_jam}': String(hoursLeft),
    '{baki_hari}': String(Math.max(0, calendarDayDiff(vars.eventDate, now))),
    '{gelanggang}': vars.gelanggang || '',
    '{tajuk}': vars.tajuk || '',
    '{lokasi}': vars.lokasi || '',
    '{nama_ahli}': vars.namaAhli || '',
  };
  return template.replace(/\{[a-z_]+\}/g, (m) => (m in map ? map[m] : m));
}

/** Upgrades legacy templates that hard-coded "esok". */
export function migrateLegacyBody(body: string): string {
  return body.replace(/\besok jam \{waktu\}/gi, '{bila} ({tarikh}) jam {waktu}');
}

export function normalizeEventTemplates(
  raw: unknown,
  legacy?: { title?: string; body?: string }
): Record<AlertEventKey, AlertEventTemplate> {
  const result: Record<AlertEventKey, AlertEventTemplate> = JSON.parse(JSON.stringify(DEFAULT_EVENT_TEMPLATES));
  let parsed: any = raw;
  if (typeof raw === 'string') {
    try { parsed = JSON.parse(raw); } catch { parsed = null; }
  }
  if (!parsed && legacy) {
    if (legacy.title) result.latihan.title = legacy.title;
    if (legacy.body) result.latihan.body = migrateLegacyBody(legacy.body);
  }
  if (parsed && typeof parsed === 'object') {
    for (const t of ALERT_EVENT_TYPES) {
      const v = parsed[t.key];
      if (v && typeof v === 'object') {
        if (typeof v.title === 'string' && v.title.trim()) result[t.key].title = v.title;
        if (typeof v.body === 'string' && v.body.trim()) result[t.key].body = migrateLegacyBody(v.body);
      }
    }
  }
  return result;
}
